import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-e7fce75e"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "qr-modal-tab" }
const _hoisted_2 = { class: "qr-modal-tab code-container" }
const _hoisted_3 = ["src"]

import { computed, inject, watch, ref } from "vue";
import ModalBase from "@/components/ui/modals/modal-base.vue";
import { DxToolbarItem } from "devextreme-vue/popup";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { AcquaintApiClient } from "@/services/AcquaintApiClient";
import { ModalWidth } from "@/types/ui/modals/ModalWidth";
import {
  copyBlobImageToClipboard,
  printBlobImage,
} from "@/utils/files/BlobImageUtils";
import { DxTabPanel, DxItem } from "devextreme-vue/tab-panel";
import FieldValidationFreeTextArea from "@/components/ui/inputs/field-validation-free-text-area.vue";
import { DxContextMenu } from "devextreme-vue/context-menu";
import { ContextMenuItem } from "@/types/ui/menus/ContextMenuItem";


export default /*#__PURE__*/_defineComponent({
  __name: 'qr-code-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * The unique identifier for the property.
   * @type {number}
   * @default -1
   */
  propertyId: {
    type: Number,
    default: -1,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes.
   * This is used to update the v-model variable in the parent component and control the visibility of the modal.
   * @event update:modelValue
   * @property {boolean} value - The updated state to reflect whether the modal should be shown or hidden.
   */
  "update:modelValue",
],
  setup(__props, { emit }) {

const props = __props;




const apiClient = inject("AcquaintApiClient") as AcquaintApiClient;
const url = ref("");
const qrCode = ref<Blob>();
const textForCodeGeneration = ref("");
const selectedTab = ref(0);
const contextMenuId = ref("qr-code-generator-modal");
const showFunctionsMenu = ref(false);

const showModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

watch(
  () => showModal.value,
  async () => {
    if (!showModal.value) {
      cleanUpQRCode();
      return;
    }

    if (props.propertyId > -1) {
      selectedTab.value = 1;
      await createQrCode();
    } else {
      selectedTab.value = 0;
    }
  },
);

/*** Creates a QR Code image by using createObjectURL to display data returned by api */
const createQrCode = async () => {
  // We want the qr code tab to be displayed automatically if both tabs can be accessed by the user
  selectedTab.value = 1;
  qrCode.value =
    props.propertyId > -1
      ? await getPropertyQrCode()
      : await createQRUsingText();

  if (!qrCode.value) {
    url.value = "";
    return;
  }

  url.value = URL.createObjectURL(qrCode.value);
};

const getPropertyQrCode = async () => {
  try {
    return await apiClient.getFile(`app/qrcode/property/${props.propertyId}`);
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return;
  }
};

const createQRUsingText = () => {
  try {
    return apiClient.getFile(
      `app/qrcode/custom?data=${textForCodeGeneration.value}`,
    );
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    return;
  }
};

/*** Releases memory used by creating an object url to display the QR Code
 *   See https://developer.mozilla.org/en-US/docs/Web/API/URL/revokeObjectURL_static */
const cleanUpQRCode = () => {
  if (!url.value) {
    return;
  }

  URL.revokeObjectURL(url.value);
  url.value = "";
  qrCode.value = undefined;
};

const onCopyClick = () => {
  if (!qrCode.value) {
    return;
  }

  copyBlobImageToClipboard(qrCode.value, false);
};

const onPrintClick = () => {
  if (!qrCode.value) {
    return;
  }

  printBlobImage(qrCode.value, "Print QR Code");
};

// This code handles the footer action menu.
const menuItems = computed(() => {
  const menu: ContextMenuItem[] = [
    {
      text: "Create",
      key: "create",
      disabled: textForCodeGeneration.value == "",
      icon: "",
    },
    {
      text: "Copy",
      key: "copy",
      disabled: url.value == "",
      icon: "",
    },
    {
      text: "Print",
      key: "print",
      disabled: url.value == "",
      icon: "",
    },
  ];

  return menu;
});

const onMenuItemClick = (e: any) => {
  switch (e.itemData.key) {
    case "create":
      createQrCode();
      break;
    case "copy":
      onCopyClick();
      break;
    case "print":
      onPrintClick();
      break;
  }
};

const onCancelClick = () => (showModal.value = false);

const copyPrintButtonPositions = computed(() => {
  return props.propertyId == -1 ? "before" : "after";
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(ModalBase, {
      modelValue: showModal.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showModal).value = $event)),
      title: "QR Code Generator",
      width: _unref(ModalWidth).XSmall,
      height: "370px"
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(DxTabPanel), {
          "scroll-by-content": true,
          "show-nav-buttons": true,
          "swipe-enabled": false,
          selectedIndex: selectedTab.value,
          "onUpdate:selectedIndex": _cache[1] || (_cache[1] = ($event: any) => ((selectedTab).value = $event))
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(DxItem), {
              title: "Text",
              visible: props.propertyId == -1
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_1, [
                  _createVNode(FieldValidationFreeTextArea, {
                    height: "11rem",
                    label: "Text To Encode",
                    modelValue: textForCodeGeneration.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((textForCodeGeneration).value = $event))
                  }, null, 8, ["modelValue"])
                ])
              ]),
              _: 1
            }, 8, ["visible"]),
            _createVNode(_unref(DxItem), { title: "QR Code" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  (url.value)
                    ? (_openBlock(), _createElementBlock("img", {
                        key: 0,
                        src: url.value,
                        width: "205",
                        height: "205"
                      }, null, 8, _hoisted_3))
                    : _createCommentVNode("", true)
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["selectedIndex"])
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          elementAttr: { id: contextMenuId.value },
          text: 'Actions',
          onClick: () => {
            showFunctionsMenu.value = !showFunctionsMenu.value;
          },
          visible: props.propertyId == -1,
        }
        }, null, 8, ["options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: copyPrintButtonPositions.value,
          options: {
          text: 'Copy',
          onClick: () => {
            onCopyClick();
          },
          visible: props.propertyId > -1,
        }
        }, null, 8, ["location", "options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: copyPrintButtonPositions.value,
          options: {
          text: 'Print',
          onClick: () => {
            onPrintClick();
          },
          visible: props.propertyId > -1,
        }
        }, null, 8, ["location", "options"]),
        _createVNode(_unref(DxToolbarItem), {
          widget: "dxButton",
          toolbar: "bottom",
          location: "after",
          options: {
          text: 'Cancel',
          onClick: () => {
            onCancelClick();
          },
        }
        }, null, 8, ["options"])
      ]),
      _: 1
    }, 8, ["modelValue", "width"]),
    _createVNode(_unref(DxContextMenu), {
      visible: showFunctionsMenu.value,
      "onUpdate:visible": _cache[3] || (_cache[3] = ($event: any) => ((showFunctionsMenu).value = $event)),
      "data-source": menuItems.value,
      target: `#${contextMenuId.value}`,
      position: { my: 'bottom right', at: 'top right' },
      onItemClick: onMenuItemClick
    }, null, 8, ["visible", "data-source", "target"])
  ], 64))
}
}

})